import {SetValueConfig, UseFormReturn} from 'react-hook-form';

import {calculateCo2Class} from '../../../kit/utils/calculateCo2Class';
import {BaseVehicle} from '../../types/BaseVehicle';

export interface FormAutofillCo2ClassActionParams {
  form: UseFormReturn<BaseVehicle>;
  emissionField:
    | 'carbonDioxideEmission'
    | 'carbonDioxideEmissionDischarged'
    | 'carbonDioxideEmissionWeightedCombined';
  classField:
    | 'carbonDioxideEmissionClass'
    | 'carbonDioxideEmissionClassDischarged'
    | 'carbonDioxideEmissionClassWeightedCombined';
  value: string | null;
}

const DEFAULT_OPTIONS = {
  shouldDirty: true,
  shouldValidate: true,
  shouldTouch: true,
} satisfies SetValueConfig;

/**
 * XState action for auto-filling CO2 emission class fields (A-G) based on emission values.
 *
 * Automatically calculates and updates the CO2 class whenever the emission value changes.
 * The class field is updated dynamically as the user types (e.g., 1 → A, 12 → B, 120 → D).
 * If the emission value is cleared, the class field is also cleared.
 *
 * @param _context - XState context (unused)
 * @param params - Parameters containing form instance, field names, and emission value
 */
export const formAutofillCo2ClassAction = (
  _context: unknown,
  params: FormAutofillCo2ClassActionParams
): void => {
  const {form, classField, value} = params;

  // Calculate CO2 class from emission value
  const calculatedClass = calculateCo2Class(value);

  // Always update the class field when we have a valid calculated class
  // This ensures the class updates dynamically as the user types the emission value
  if (calculatedClass != null) {
    form.setValue(classField, calculatedClass, DEFAULT_OPTIONS);
  } else {
    // Clear the class field if emission value is null/empty
    const currentClassValue = form.getValues(classField);
    if (currentClassValue != null) {
      form.setValue(classField, null, DEFAULT_OPTIONS);
    }
  }
};
